/**
 * Proposal Assistant Page
 * 
 * AI-powered proposal preparation tool that orchestrates:
 * - Methodology generation via n8n workflow
 * - Cost estimation calculator
 * - Source references and evaluation questions
 * 
 * CV matching is available via the dedicated ToR CV Search page.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box, Paper, Typography, Button, Alert, Grid, LinearProgress,
    Chip, Divider, Card, CardContent, IconButton, Tooltip, Fade, Zoom,
    Stack, Avatar, Accordion, AccordionSummary, AccordionDetails,
    Badge, CircularProgress, Collapse, ButtonGroup, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl, InputAdornment
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    AutoAwesome as AutoAwesomeIcon,
    Psychology as PsychologyIcon,
    Groups as GroupsIcon,
    Business as BusinessIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    ExpandMore as ExpandMoreIcon,
    ContentCopy as ContentCopyIcon,
    Refresh as RefreshIcon,
    Description as DescriptionIcon,
    TrendingUp as TrendingUpIcon,
    Speed as SpeedIcon,
    Lightbulb as LightbulbIcon,
    Tune as TuneIcon,
    PlayArrow as PlayArrowIcon,
    Calculate as CalculateIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon,
    Source as SourceIcon,
    QuestionAnswer as QuestionAnswerIcon,
    Public as PublicIcon,
    Schedule as ScheduleIcon,
    AttachMoney as AttachMoneyIcon,
    Assessment as AssessmentIcon,
    HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import {
    runProposalAssistantWithRequirements,
    ProposalAssistantProgress,
    ProposalAssistantResult,
    ProgressState,
    formatFileSize,
    RequirementTiers,
    DEFAULT_TIERS,
    pollForMethodologyResult,
    listTorExtractions,
    deleteTorExtraction,
    getFullTorExtraction,
    saveTorExtraction,
    TorExtractionSummary,
    buildFilteredRequirementsV2,
    generateSessionId
} from '../services/proposalAssistant';
import { TorForCVsExtractionResult } from '../services/torForCVs';
import { getClientPrincipal } from '../services/auth';
import ReactMarkdown from 'react-markdown';

// Types for V2 per-item tier control
type RequirementTier = 'required' | 'preferred' | 'bonus';
interface TieredItem { text: string; tier: RequirementTier; }

// Status icon component
const StatusIcon: React.FC<{ status: ProgressState['status'] }> = ({ status }) => {
    switch (status) {
        case 'complete': return <CheckCircleIcon sx={{ color: 'success.main' }} />;
        case 'error': return <ErrorIcon sx={{ color: 'error.main' }} />;
        case 'running': return <CircularProgress size={24} />;
        default: return <PendingIcon sx={{ color: 'text.disabled' }} />;
    }
};

// Progress card component
const ProgressCard: React.FC<{
    title: string;
    icon: React.ReactNode;
    state: ProgressState;
    color: string;
    onRetry?: () => void;
    isRetrying?: boolean;
}> = ({ title, icon, state, color, onRetry, isRetrying }) => {
    const duration = state.endTime && state.startTime 
        ? ((state.endTime - state.startTime) / 1000).toFixed(1) : null;
    
    return (
        <Card elevation={state.status === 'running' ? 8 : 2}
            sx={{ 
                height: '100%', transition: 'all 0.3s ease',
                border: state.status === 'running' ? `2px solid ${color}` : '1px solid transparent',
                background: state.status === 'complete' 
                    ? `linear-gradient(135deg, ${color}08 0%, ${color}15 100%)`
                    : state.status === 'error' ? 'linear-gradient(135deg, #ff000008 0%, #ff000015 100%)' : 'inherit'
            }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: color, mr: 2 }}>{icon}</Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
                        <Typography variant="caption" color="text.secondary">{state.message}</Typography>
                    </Box>
                    <StatusIcon status={state.status} />
                </Box>
                <LinearProgress 
                    variant={state.status === 'running' && state.progress < 10 ? 'indeterminate' : 'determinate'}
                    value={state.progress}
                    sx={{ height: 8, borderRadius: 4, bgcolor: `${color}20`,
                        '& .MuiLinearProgress-bar': { bgcolor: state.status === 'error' ? 'error.main' : color, borderRadius: 4 }
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">{state.progress}%</Typography>
                    {duration && <Typography variant="caption" color="text.secondary">{duration}s</Typography>}
                </Box>
                {state.status === 'error' && onRetry && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onRetry}
                        disabled={isRetrying}
                        startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
                        sx={{ mt: 1, width: '100%' }}
                    >
                        {isRetrying ? 'Checking...' : 'Check Again'}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

const ProposalAssistantPage: React.FC = () => {
    // Mode state - search existing or upload new
    const [inputMode, setInputMode] = useState<'search' | 'upload'>('search');
    const [existingTors, setExistingTors] = useState<TorExtractionSummary[]>([]);
    const [selectedTorId, setSelectedTorId] = useState<string | null>(null);
    const [torSearchQuery, setTorSearchQuery] = useState('');
    const [isLoadingTors, setIsLoadingTors] = useState(false);
    const [enableCVSearch, setEnableCVSearch] = useState(false); // CV search is optional bonus feature
    
    // File state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // Extraction state (Step 1)
    const [isExtracting, setIsExtracting] = useState(false);
    const [torRequirements, setTorRequirements] = useState<TorForCVsExtractionResult | null>(null);
    
    // Requirements customization state (Step 2) - V2 with per-item tiers
    const [tieredItems, setTieredItems] = useState<{[category: string]: TieredItem[]}>({});
    const [newItemInputs, setNewItemInputs] = useState<{[category: string]: string}>({});
    const [showCustomize, setShowCustomize] = useState(false);
    
    // Legacy state for backward compatibility
    const [tiers, setTiers] = useState<RequirementTiers>({ ...DEFAULT_TIERS });
    const [selectedItems, setSelectedItems] = useState<{[category: string]: Set<string>}>({});
    
    // Processing state (Step 3)
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState<ProposalAssistantProgress | null>(null);
    const [result, setResult] = useState<ProposalAssistantResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [copiedMethodology, setCopiedMethodology] = useState(false);
    const [copiedCosts, setCopiedCosts] = useState(false);
    const [activeTab, setActiveTab] = useState<'overview' | 'methodology' | 'costs' | 'sources'>('overview');
    const [userEmail, setUserEmail] = useState<string>('');
    const [isRetryingMethodology, setIsRetryingMethodology] = useState(false);
    const [lastSessionId, setLastSessionId] = useState<string | null>(null);
    const [currentTorSessionId, setCurrentTorSessionId] = useState<string | null>(null);
    
    // Cost calculator state - dynamic consultants with editable rates
    const ROLE_OPTIONS = [
        { value: 'principal', label: 'Principal', defaultRate: 900 },
        { value: 'principal_associate', label: 'Principal Associate', defaultRate: 750 },
        { value: 'senior', label: 'Senior Consultant', defaultRate: 600 },
        { value: 'senior_associate', label: 'Senior Associate', defaultRate: 500 },
        { value: 'consultant', label: 'Consultant', defaultRate: 400 },
        { value: 'associate', label: 'Associate', defaultRate: 300 },
        { value: 'analyst', label: 'Analyst', defaultRate: 200 }
    ];
    
    interface ConsultantRow {
        id: string;
        role: string;
        label: string;
        rate: number;
        days: number;
    }
    
    const [consultants, setConsultants] = useState<ConsultantRow[]>([
        { id: '1', role: 'principal', label: 'Principal', rate: 900, days: 0 },
        { id: '2', role: 'senior', label: 'Senior Consultant', rate: 600, days: 0 },
        { id: '3', role: 'consultant', label: 'Consultant', rate: 400, days: 0 },
        { id: '4', role: 'analyst', label: 'Analyst', rate: 200, days: 0 }
    ]);
    
    const [costLineItems, setCostLineItems] = useState<{name: string; amount: number}[]>([
        { name: 'Travel & Accommodation', amount: 0 },
        { name: 'Sub-contractors', amount: 0 },
        { name: 'Overhead (20%)', amount: 0 },
        { name: 'Contingency (5%)', amount: 0 }
    ]);
    
    // Currency state for cost calculator - major world currencies
    const CURRENCY_OPTIONS = [
        { code: 'GBP', symbol: '£', label: 'GBP (£)' },
        { code: 'USD', symbol: '$', label: 'USD ($)' },
        { code: 'EUR', symbol: '€', label: 'EUR (€)' },
        { code: 'CHF', symbol: 'CHF ', label: 'CHF' },
        { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
        { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
        { code: 'JPY', symbol: '¥', label: 'JPY (¥)' },
        { code: 'CNY', symbol: '¥', label: 'CNY (¥)' },
        { code: 'INR', symbol: '₹', label: 'INR (₹)' },
        { code: 'SEK', symbol: 'kr', label: 'SEK (kr)' },
        { code: 'NOK', symbol: 'kr', label: 'NOK (kr)' },
        { code: 'DKK', symbol: 'kr', label: 'DKK (kr)' },
        { code: 'ZAR', symbol: 'R', label: 'ZAR (R)' },
        { code: 'AED', symbol: 'د.إ', label: 'AED' },
        { code: 'SAR', symbol: '﷼', label: 'SAR' },
        { code: 'SGD', symbol: 'S$', label: 'SGD (S$)' },
        { code: 'HKD', symbol: 'HK$', label: 'HKD (HK$)' },
        { code: 'NZD', symbol: 'NZ$', label: 'NZD (NZ$)' },
        { code: 'MXN', symbol: 'Mex$', label: 'MXN' },
        { code: 'BRL', symbol: 'R$', label: 'BRL (R$)' }
    ];
    const [selectedCurrency, setSelectedCurrency] = useState<string>('GBP');
    const getCurrencySymbol = () => CURRENCY_OPTIONS.find(c => c.code === selectedCurrency)?.symbol || '£';
    
    // Exchange rates relative to GBP (base currency for default rates)
    // These are approximate rates - consider fetching live rates for production
    const [exchangeRates, setExchangeRates] = useState<{[key: string]: number}>({
        GBP: 1.00,
        USD: 1.27,
        EUR: 1.17,
        CHF: 1.12,
        CAD: 1.72,
        AUD: 1.93,
        JPY: 191.50,
        CNY: 9.15,
        INR: 106.00,
        SEK: 13.20,
        NOK: 13.50,
        DKK: 8.70,
        ZAR: 23.00,
        AED: 4.66,
        SAR: 4.76,
        SGD: 1.70,
        HKD: 9.90,
        NZD: 2.10,
        MXN: 21.80,
        BRL: 7.30
    });
    const [isLoadingRates, setIsLoadingRates] = useState(false);
    const [ratesLastUpdated, setRatesLastUpdated] = useState<Date | null>(null);
    
    // Fetch live exchange rates from API
    const fetchExchangeRates = useCallback(async () => {
        setIsLoadingRates(true);
        try {
            // Using frankfurter.app - free, no API key required
            const response = await fetch('https://api.frankfurter.app/latest?from=GBP');
            if (response.ok) {
                const data = await response.json();
                setExchangeRates(prev => ({
                    ...prev,
                    GBP: 1.00,
                    ...data.rates
                }));
                setRatesLastUpdated(new Date());
            }
        } catch (err) {
            console.warn('Failed to fetch exchange rates, using defaults:', err);
        } finally {
            setIsLoadingRates(false);
        }
    }, []);
    
    // Fetch rates on mount
    useEffect(() => {
        fetchExchangeRates();
    }, [fetchExchangeRates]);
    
    // Handle currency change with rate conversion
    const handleCurrencyChange = useCallback((newCurrency: string) => {
        const oldCurrency = selectedCurrency;
        if (oldCurrency === newCurrency) return;
        
        const oldRate = exchangeRates[oldCurrency] || 1;
        const newRate = exchangeRates[newCurrency] || 1;
        const conversionFactor = newRate / oldRate;
        
        // Convert consultant rates
        setConsultants(prev => prev.map(c => ({
            ...c,
            rate: Math.round(c.rate * conversionFactor)
        })));
        
        // Convert additional cost amounts
        setCostLineItems(prev => prev.map(item => ({
            ...item,
            amount: Math.round(item.amount * conversionFactor)
        })));
        
        setSelectedCurrency(newCurrency);
    }, [selectedCurrency, exchangeRates]);
    
    // Investment band calculation based on GBP equivalent
    const getInvestmentBand = (total: number) => {
        // Convert to GBP for consistent band calculation
        const currentRate = exchangeRates[selectedCurrency] || 1;
        const gbpTotal = total / currentRate;
        
        if (gbpTotal < 100000) return 'Small';
        if (gbpTotal <= 600000) return 'Medium';
        return 'Large';
    };
    
    // Legacy compatibility - map old costRoleDays format
    const costRoleDays = consultants.reduce((acc, c) => ({ ...acc, [c.role]: c.days }), {} as {[key: string]: number});
    const DAY_RATES = consultants.reduce((acc, c) => ({ ...acc, [c.role]: c.rate }), {} as {[key: string]: number});
    
    // Initialize cost calculator when result changes
    useEffect(() => {
        if (result?.methodology?.roleDays) {
            const rd = result.methodology.roleDays;
            setConsultants(prev => prev.map(c => ({
                ...c,
                days: rd[c.role as keyof typeof rd] || 0
            })));
        }
        if (result?.methodology?.costBreakdown) {
            const cb = result.methodology.costBreakdown;
            setCostLineItems([
                { name: 'Travel & Accommodation', amount: cb.travel_accommodation || 0 },
                { name: 'Sub-contractors', amount: cb.sub_contractors || 0 },
                { name: 'Overhead (20%)', amount: cb.overhead || 0 },
                { name: 'Contingency (5%)', amount: cb.contingency || 0 }
            ]);
        }
    }, [result?.methodology?.roleDays, result?.methodology?.costBreakdown]);
    
    // Calculate totals
    const calculateConsultantCost = () => {
        return consultants.reduce((sum, c) => sum + (c.days * c.rate), 0);
    };
    
    const calculateTotalDays = () => {
        return consultants.reduce((sum, c) => sum + c.days, 0);
    };
    
    const calculateLineItemsTotal = () => {
        return costLineItems.reduce((sum, item) => sum + item.amount, 0);
    };
    
    const calculateGrandTotal = () => {
        return calculateConsultantCost() + calculateLineItemsTotal();
    };
    
    // Fetch user email from auth on mount
    useEffect(() => {
        const fetchUserEmail = async () => {
            try {
                const principal = await getClientPrincipal();
                if (principal?.userDetails) {
                    setUserEmail(principal.userDetails);
                }
            } catch (err) {
                console.error('Failed to get user email:', err);
            }
        };
        fetchUserEmail();
    }, []);
    
    // Load existing ToRs on mount and when search changes
    useEffect(() => {
        const loadTors = async () => {
            setIsLoadingTors(true);
            try {
                const tors = await listTorExtractions(torSearchQuery || undefined, 50);
                setExistingTors(tors);
            } catch (err) {
                console.error('Failed to load ToRs:', err);
            } finally {
                setIsLoadingTors(false);
            }
        };
        
        // Debounce search
        const timeoutId = setTimeout(loadTors, 300);
        return () => clearTimeout(timeoutId);
    }, [torSearchQuery]);
    
    // Handle selecting an existing ToR
    const handleSelectExistingTor = useCallback(async (sessionId: string) => {
        setSelectedTorId(sessionId);
        setIsExtracting(true);
        setError(null);
        
        try {
            // Use pollForMethodologyResult - same function used after n8n workflow completes
            // This ensures consistent data formatting between new and existing ToRs
            const methodologyResult = await pollForMethodologyResult(sessionId);
            
            if (methodologyResult) {
                // Methodology exists - show results directly
                console.log('[Proposal Assistant] Loaded existing methodology:', methodologyResult);
                
                setLastSessionId(sessionId);
                
                // Set progress to "complete" state so UI shows results
                setProgress({
                    torExtraction: { status: 'complete', progress: 100, message: 'Loaded from database' },
                    methodology: { status: 'complete', progress: 100, message: 'Loaded from database' },
                    associateCVs: { status: 'complete', progress: 100, message: 'Not searched' },
                    iodStaffCVs: { status: 'complete', progress: 100, message: 'Not searched' }
                });
                
                // Set result - same structure as when n8n workflow completes
                setResult({
                    sessionId,
                    methodology: methodologyResult,
                    torExtraction: null, // ToR data is embedded in methodology
                    associateCVs: null,
                    iodStaffCVs: null,
                    errors: {}
                } as ProposalAssistantResult);
                
                // Also load full ToR extraction for CV search functionality
                try {
                    const sections = await getFullTorExtraction(sessionId);
                    console.log('[Proposal Assistant] Full ToR sections loaded:', Object.keys(sections || {}));
                    // sections is keyed by section_type: { tor_extraction: {...}, methodology: {...}, ... }
                    const torSection = sections?.tor_extraction;
                    if (torSection && torSection.content_json) {
                        console.log('[Proposal Assistant] Loaded ToR requirements for CV search:', torSection.content_json);
                        setTorRequirements({
                            requirements: torSection.content_json,
                            query_vector: undefined, // Query vector not stored in DB
                            full_tor_text: '',
                            project_title: methodologyResult.projectTitle || torSection.project_title,
                            client_name: methodologyResult.clientName || torSection.client_name
                        });
                    } else {
                        console.warn('[Proposal Assistant] No tor_extraction section found or content_json is empty');
                    }
                } catch (torErr) {
                    console.warn('[Proposal Assistant] Could not load ToR requirements for CV search:', torErr);
                    // Non-fatal - CV search just won't have pre-filled data
                }
                
            } else {
                // No methodology yet - this shouldn't happen for entries with has_methodology=true
                // But handle gracefully by showing error
                throw new Error('Methodology not found for this ToR. It may still be processing.');
            }
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load ToR');
        } finally {
            setIsExtracting(false);
        }
    }, []);
    
    // Handle deleting a ToR
    const handleDeleteTor = useCallback(async (sessionId: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Don't select the row
        if (!window.confirm('Delete this ToR and all related data?')) return;
        
        try {
            await deleteTorExtraction(sessionId);
            setExistingTors(prev => prev.filter(t => t.session_id !== sessionId));
            if (selectedTorId === sessionId) {
                setSelectedTorId(null);
                setTorRequirements(null);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete ToR');
        }
    }, [selectedTorId]);
    
    // Open CV Search page with pre-populated requirements
    const handleOpenCVSearch = useCallback(() => {
        // Try to get requirements from torRequirements first, then fall back to result
        let requirements = torRequirements?.requirements;
        let queryVector = torRequirements?.query_vector;
        
        // If no torRequirements but we have result with methodology, open without pre-fill
        // The CV search page can still be used manually
        if (!requirements && !result?.methodology) {
            console.warn('[Proposal Assistant] No requirements available for CV search');
            // Still open the page - user can search manually
            const baseUrl = window.location.origin + window.location.pathname;
            window.open(`${baseUrl}#/tor-cv-search`, '_blank');
            return;
        }
        
        if (requirements) {
            // Build filtered requirements with tiered items applied
            const tieredItemsForService: { [category: string]: Array<{ text: string; tier: string }> } = {};
            Object.entries(tieredItems).forEach(([cat, items]) => {
                tieredItemsForService[cat] = items.map(i => ({ text: i.text, tier: i.tier }));
            });
            
            // Apply tiered items to requirements
            const filteredRequirements = Object.keys(tieredItemsForService).length > 0
                ? buildFilteredRequirementsV2(requirements, tieredItemsForService)
                : requirements;
            
            // Store requirements in localStorage for the CV search page to pick up
            const cvSearchData = {
                requirements: filteredRequirements,
                queryVector: queryVector,
                timestamp: Date.now(),
                source: 'proposal-assistant'
            };
            localStorage.setItem('cv-search-prefill', JSON.stringify(cvSearchData));
        }
        
        // Open ToR CV Search page in new tab (use full URL for HashRouter)
        const baseUrl = window.location.origin + window.location.pathname;
        window.open(`${baseUrl}#/tor-cv-search`, '_blank');
    }, [torRequirements, tieredItems, result]);
    
    // File selection handler
    const handleFileSelect = useCallback((file: File) => {
        if (!file.name.toLowerCase().endsWith('.pdf')) { setError('Please select a PDF file'); return; }
        if (file.size > 20 * 1024 * 1024) { setError('File size must be less than 20MB'); return; }
        setSelectedFile(file); setError(null); setResult(null); setProgress(null);
        setTorRequirements(null); setSelectedItems({});
    }, []);
    
    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelect(file);
    }, [handleFileSelect]);
    
    const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
    const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); }, []);
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault(); setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
    }, [handleFileSelect]);
    
    // Step 1: Prepare ToR for methodology generation (skip CV-specific extraction)
    const handleExtractRequirements = useCallback(async () => {
        if (!selectedFile) return;
        setIsExtracting(true); setError(null);
        try {
            // Just create a minimal extraction result - n8n will do the full extraction
            // We only need the file ready for the methodology workflow
            const minimalExtraction: TorForCVsExtractionResult = {
                requirements: {} as any,
                full_tor_text: '',
                project_title: selectedFile.name.replace(/\.pdf$/i, ''),
                client_name: ''
            };
            
            setTorRequirements(minimalExtraction);
            
            // Generate a session ID for this ToR
            const newSessionId = generateSessionId();
            setCurrentTorSessionId(newSessionId);
            
            console.log('ToR ready for methodology generation, session:', newSessionId);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to prepare ToR');
        } finally { setIsExtracting(false); }
    }, [selectedFile]);
    
    // Step 3: Run full processing with customized requirements
    const handleStartProcessing = useCallback(async () => {
        if (!selectedFile || !torRequirements) return;
        setIsProcessing(true); setError(null); setResult(null);
        try {
            // Convert tieredItems to the format expected by the service
            const tieredItemsForService: { [category: string]: Array<{ text: string; tier: string }> } = {};
            Object.entries(tieredItems).forEach(([cat, items]) => {
                tieredItemsForService[cat] = items.map(i => ({ text: i.text, tier: i.tier }));
            });
            
            const assistantResult = await runProposalAssistantWithRequirements(
                selectedFile, userEmail, torRequirements, tiers, selectedItems, setProgress, tieredItemsForService, enableCVSearch
            );
            setResult(assistantResult);
            // Capture session ID for retry functionality
            if (assistantResult.sessionId) {
                setLastSessionId(assistantResult.sessionId);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        } finally { setIsProcessing(false); }
    }, [selectedFile, torRequirements, tiers, selectedItems, userEmail, tieredItems, enableCVSearch]);
    
    // Reset everything
    const handleReset = useCallback(() => {
        setSelectedFile(null); setProgress(null); setResult(null); setError(null);
        setIsProcessing(false); setIsExtracting(false); setTorRequirements(null);
        setSelectedItems({}); setShowCustomize(false); setLastSessionId(null);
        setCurrentTorSessionId(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);
    
    // Retry methodology check - manually poll for result that may have completed after timeout
    const handleRetryMethodology = useCallback(async () => {
        if (!lastSessionId) {
            console.warn('[Proposal Assistant] No session ID available for retry');
            return;
        }
        
        setIsRetryingMethodology(true);
        console.log(`[Proposal Assistant] Retrying methodology check for session: ${lastSessionId}`);
        
        try {
            const methodologyResult = await pollForMethodologyResult(lastSessionId);
            
            if (methodologyResult) {
                console.log('[Proposal Assistant] ✅ Found methodology on retry!', methodologyResult);
                // Update result with the found methodology
                setResult(prev => prev ? { ...prev, methodology: methodologyResult, errors: { ...prev.errors, methodology: undefined } } : null);
                // Update progress to show success
                setProgress(prev => prev ? {
                    ...prev,
                    methodology: {
                        ...prev.methodology,
                        status: 'complete',
                        progress: 100,
                        message: `Complete! Score: ${methodologyResult.alignmentScore}%`,
                        endTime: Date.now()
                    }
                } : null);
            } else {
                console.log('[Proposal Assistant] Methodology still not found in database');
                // Update message to indicate it's still processing
                setProgress(prev => prev ? {
                    ...prev,
                    methodology: {
                        ...prev.methodology,
                        message: 'Still processing... Try again in a moment.'
                    }
                } : null);
            }
        } catch (err) {
            console.error('[Proposal Assistant] Retry error:', err);
        } finally {
            setIsRetryingMethodology(false);
        }
    }, [lastSessionId]);
    
    // Tier change handler
    const handleTierChange = (criterion: string, tier: 'required' | 'preferred' | 'bonus') => {
        setTiers({ ...tiers, [criterion]: tier });
    };
    
    // Toggle item selection
    const toggleItem = (category: string, item: string) => {
        setSelectedItems(prev => {
            const categorySet = new Set(prev[category] || []);
            if (categorySet.has(item)) { categorySet.delete(item); } 
            else { categorySet.add(item); }
            return { ...prev, [category]: categorySet };
        });
    };
    
    // Copy methodology to clipboard
    const handleCopyMethodology = useCallback(() => {
        if (result?.methodology?.generatedText) {
            navigator.clipboard.writeText(result.methodology.generatedText);
            setCopiedMethodology(true);
            setTimeout(() => setCopiedMethodology(false), 2000);
        }
    }, [result]);
    
    // Copy costs to clipboard
    const handleCopyCosts = useCallback(() => {
        const symbol = getCurrencySymbol();
        const consultantLines = consultants
            .filter(c => c.days > 0)
            .map(c => `  ${c.label} (${symbol}${c.rate}/day): ${c.days} days = ${symbol}${(c.days * c.rate).toLocaleString()}`);
        
        const lines = [
            'INDICATIVE COST ESTIMATE',
            '========================',
            `Currency: ${selectedCurrency}`,
            '',
            'Consultant Days:',
            ...consultantLines,
            `  Subtotal: ${calculateTotalDays()} days = ${getCurrencySymbol()}${calculateConsultantCost().toLocaleString()}`,
            '',
            'Additional Costs:',
            ...costLineItems.map(item => `  ${item.name}: ${getCurrencySymbol()}${item.amount.toLocaleString()}`),
            '',
            `TOTAL ESTIMATE: ${getCurrencySymbol()}${calculateGrandTotal().toLocaleString()}`,
            '',
            `Investment Band: ${getInvestmentBand(calculateGrandTotal())}`
        ];
        navigator.clipboard.writeText(lines.join('\n'));
        setCopiedCosts(true);
        setTimeout(() => setCopiedCosts(false), 2000);
    }, [consultants, costLineItems, selectedCurrency, calculateTotalDays, calculateConsultantCost, calculateGrandTotal, getCurrencySymbol, getInvestmentBand]);
    
    // Add new line item
    const handleAddLineItem = () => {
        setCostLineItems([...costLineItems, { name: 'New Item', amount: 0 }]);
    };
    
    // Remove line item
    const handleRemoveLineItem = (index: number) => {
        setCostLineItems(costLineItems.filter((_, i) => i !== index));
    };
    
    // Update line item
    const handleUpdateLineItem = (index: number, field: 'name' | 'amount', value: string | number) => {
        const updated = [...costLineItems];
        if (field === 'name') {
            updated[index].name = value as string;
        } else {
            updated[index].amount = typeof value === 'string' ? parseFloat(value) || 0 : value;
        }
        setCostLineItems(updated);
    };
    
    // Consultant management functions
    const handleUpdateConsultant = (id: string, field: keyof ConsultantRow, value: string | number) => {
        setConsultants(prev => prev.map(c => {
            if (c.id !== id) return c;
            if (field === 'role') {
                const roleOption = ROLE_OPTIONS.find(r => r.value === value);
                return { ...c, role: value as string, label: roleOption?.label || value as string, rate: roleOption?.defaultRate || c.rate };
            }
            if (field === 'rate') return { ...c, rate: Math.max(0, Number(value)) };
            if (field === 'days') return { ...c, days: Math.max(0, Number(value)) };
            return c;
        }));
    };
    
    const handleAddConsultant = () => {
        const newId = String(Date.now());
        setConsultants(prev => [...prev, { id: newId, role: 'consultant', label: 'Consultant', rate: 400, days: 0 }]);
    };
    
    const handleRemoveConsultant = (id: string) => {
        setConsultants(prev => prev.filter(c => c.id !== id));
    };
    
    const overallProgress = progress ? (progress.torExtraction.progress + progress.methodology.progress) / 2 : 0;
    
    const isComplete = progress && progress.methodology.status !== 'running';
    
    // Tier colors
    const TIER_COLORS = {
        required: { bg: '#b71c1c', text: 'white' },
        preferred: { bg: '#e65100', text: 'white' },
        bonus: { bg: '#2e7d32', text: 'white' }
    };
    
    // Cycle tier: required -> preferred -> bonus -> required
    const cycleTier = (tier: RequirementTier): RequirementTier => {
        switch (tier) {
            case 'required': return 'preferred';
            case 'preferred': return 'bonus';
            case 'bonus': return 'required';
            default: return 'required';
        }
    };
    
    // Handle chip click to cycle tier
    const handleChipTierCycle = (category: string, index: number) => {
        setTieredItems(prev => {
            const items = [...(prev[category] || [])];
            if (items[index]) {
                items[index] = { ...items[index], tier: cycleTier(items[index].tier) };
            }
            return { ...prev, [category]: items };
        });
    };
    
    // Handle chip remove (right-click)
    const handleChipRemove = (category: string, index: number) => {
        setTieredItems(prev => {
            const items = (prev[category] || []).filter((_, i) => i !== index);
            return { ...prev, [category]: items };
        });
    };
    
    // Handle add new item
    const handleAddNewItem = (category: string) => {
        const text = newItemInputs[category]?.trim();
        if (text && !(tieredItems[category] || []).some(i => i.text.toLowerCase() === text.toLowerCase())) {
            setTieredItems(prev => ({
                ...prev,
                [category]: [...(prev[category] || []), { text, tier: 'required' as RequirementTier }]
            }));
            setNewItemInputs(prev => ({ ...prev, [category]: '' }));
        }
    };
    
    // Helper to render tiered chip (V2)
    const renderTieredChip = (category: string, item: TieredItem, index: number) => {
        const colors = TIER_COLORS[item.tier];
        return (
            <Chip key={`${item.text}-${index}`} label={item.text} size="small"
                onClick={() => handleChipTierCycle(category, index)}
                onContextMenu={(e) => { e.preventDefault(); handleChipRemove(category, index); }}
                sx={{
                    cursor: 'pointer', bgcolor: colors.bg, color: colors.text,
                    '&:hover': { bgcolor: colors.bg, opacity: 0.85 }
                }}
            />
        );
    };
    
    // Helper to render category section with tiered chips and add input
    const renderTieredCategory = (category: string, label: string, placeholder: string) => {
        const items = tieredItems[category] || [];
        return (
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>{label}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1, minHeight: 32 }}>
                    {items.map((item, i) => renderTieredChip(category, item, i))}
                    {items.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No items. Add below.
                        </Typography>
                    )}
                </Stack>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField size="small" fullWidth placeholder={placeholder}
                        value={newItemInputs[category] || ''}
                        onChange={(e) => setNewItemInputs(prev => ({ ...prev, [category]: e.target.value }))}
                        onKeyPress={(e) => { if (e.key === 'Enter') handleAddNewItem(category); }}
                    />
                    <IconButton size="small" color="primary" onClick={() => handleAddNewItem(category)}
                        disabled={!newItemInputs[category]?.trim()}>
                        <AddIcon />
                    </IconButton>
                </Box>
            </Grid>
        );
    };
    
    // Legacy: Helper to render selectable chip (kept for backward compatibility)
    const renderSelectableChip = (category: string, item: string, index: number) => {
        const isSelected = selectedItems[category]?.has(item) ?? false;
        return (
            <Chip key={index} label={item} size="small" onClick={() => toggleItem(category, item)}
                color={isSelected ? 'primary' : 'default'} variant={isSelected ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer', opacity: isSelected ? 1 : 0.6, '&:hover': { opacity: 1 } }}
            />
        );
    };
    
    // Legacy: Helper to render tier selector (kept for backward compatibility)
    const renderTierSelector = (criterion: string) => (
        <ButtonGroup size="small" variant="outlined" sx={{ mt: 1 }}>
            {(['required', 'preferred', 'bonus'] as const).map(tier => (
                <Button key={tier}
                    variant={tiers[criterion] === tier ? 'contained' : 'outlined'}
                    onClick={() => handleTierChange(criterion, tier)}
                    sx={{ 
                        bgcolor: tiers[criterion] === tier ? (tier === 'required' ? '#b71c1c' : tier === 'preferred' ? '#e65100' : '#2e7d32') : 'transparent',
                        color: tiers[criterion] === tier ? 'white' : (tier === 'required' ? '#b71c1c' : tier === 'preferred' ? '#e65100' : '#2e7d32'),
                        borderColor: tier === 'required' ? '#b71c1c' : tier === 'preferred' ? '#e65100' : '#2e7d32',
                        textTransform: 'capitalize', fontSize: '0.7rem', px: 1
                    }}>
                    {tier}
                </Button>
            ))}
        </ButtonGroup>
    );

    return (
        <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
            <>
                {/* Header */}
                <Fade in timeout={500}>
                    <Box sx={{ textAlign: 'center', mb: 5 }}>
                        <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', mx: 'auto', mb: 2,
                            boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)' }}>
                            <AutoAwesomeIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Typography variant="h3" fontWeight={700} sx={{ 
                            background: 'linear-gradient(135deg, #1976d2 0%, #7c4dff 100%)',
                            backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1
                        }}>
                            Proposal Assistant
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                            AI-powered proposal preparation. Upload your ToR and get methodology guidance and cost estimates in minutes.
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
                            <Chip icon={<PsychologyIcon />} label="AI Methodology" size="small" variant="outlined" sx={{ borderColor: 'divider', color: 'text.secondary' }} />
                            <Chip icon={<CalculateIcon />} label="Cost Estimates" size="small" variant="outlined" sx={{ borderColor: 'divider', color: 'text.secondary' }} />
                            <Chip icon={<SourceIcon />} label="Source References" size="small" variant="outlined" sx={{ borderColor: 'divider', color: 'text.secondary' }} />
                        </Stack>
                    </Box>
                </Fade>
                
                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
                
                {/* ToR Selection Section - Step 1 */}
                {!isProcessing && !result && !torRequirements && (
                    <Zoom in timeout={300}>
                        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                            {/* Mode Toggle */}
                            <Box sx={{ display: 'flex', mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                                <Button
                                    sx={{ flex: 1, py: 1.5, borderRadius: 0, borderBottom: inputMode === 'search' ? 3 : 0, borderColor: 'primary.main' }}
                                    startIcon={<DescriptionIcon />}
                                    onClick={() => setInputMode('search')}
                                    color={inputMode === 'search' ? 'primary' : 'inherit'}
                                >
                                    Select Existing ToR
                                </Button>
                                <Button
                                    sx={{ flex: 1, py: 1.5, borderRadius: 0, borderBottom: inputMode === 'upload' ? 3 : 0, borderColor: 'primary.main' }}
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => setInputMode('upload')}
                                    color={inputMode === 'upload' ? 'primary' : 'inherit'}
                                >
                                    Upload New ToR
                                </Button>
                            </Box>
                            
                            {/* Search Existing ToRs */}
                            {inputMode === 'search' && (
                                <Box>
                                    <TextField
                                        fullWidth
                                        placeholder="Search by project title, client, or filename..."
                                        value={torSearchQuery}
                                        onChange={(e) => setTorSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start"><DescriptionIcon color="action" /></InputAdornment>
                                        }}
                                        sx={{ mb: 2 }}
                                    />
                                    
                                    {isLoadingTors ? (
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <CircularProgress size={32} />
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading ToRs...</Typography>
                                        </Box>
                                    ) : existingTors.length === 0 ? (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            No existing ToRs found. Upload a new one to get started.
                                        </Alert>
                                    ) : (
                                        <TableContainer sx={{ maxHeight: 350 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600 }}>Project Title</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {existingTors.map((tor) => (
                                                        <TableRow 
                                                            key={tor.session_id}
                                                            hover
                                                            selected={selectedTorId === tor.session_id}
                                                            onClick={() => setSelectedTorId(tor.session_id)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {tor.project_title || tor.source_filename || 'Untitled'}
                                                                </Typography>
                                                                {tor.source_filename && tor.project_title && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {tor.source_filename}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{tor.client_name || '-'}</TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption">
                                                                    {new Date(tor.uploaded_at).toLocaleDateString()}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <IconButton size="small" color="error" onClick={(e) => handleDeleteTor(tor.session_id, e)}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                    
                                    {selectedTorId && (
                                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={isExtracting ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                                                onClick={() => handleSelectExistingTor(selectedTorId)}
                                                disabled={isExtracting}
                                            >
                                                {isExtracting ? 'Loading ToR...' : 'Use Selected ToR'}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                            
                            {/* Upload New ToR */}
                            {inputMode === 'upload' && (
                                <>
                                    <Box onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                                        onClick={() => !isExtracting && fileInputRef.current?.click()}
                                        sx={{
                                            border: '2px dashed', borderRadius: 2, p: 6, textAlign: 'center', 
                                            cursor: isExtracting ? 'wait' : 'pointer',
                                            borderColor: isDragging ? 'primary.main' : selectedFile ? 'success.main' : 'divider',
                                            transition: 'all 0.3s ease',
                                            bgcolor: 'background.default',
                                            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                                        }}>
                                        <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileInputChange} style={{ display: 'none' }} />
                                        {selectedFile ? (
                                            <Box>
                                                <Avatar sx={{ width: 64, height: 64, bgcolor: 'success.main', mx: 'auto', mb: 2 }}>
                                                    <DescriptionIcon sx={{ fontSize: 32 }} />
                                                </Avatar>
                                                <Typography variant="h6" fontWeight={600} gutterBottom>{selectedFile.name}</Typography>
                                                <Typography variant="body2" color="text.secondary">{formatFileSize(selectedFile.size)}</Typography>
                                                {!isExtracting && (
                                                    <Button variant="text" size="small" sx={{ mt: 1 }} onClick={(e) => { e.stopPropagation(); handleReset(); }}>
                                                        Change File
                                                    </Button>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.light', mx: 'auto', mb: 2 }}>
                                                    <CloudUploadIcon sx={{ fontSize: 32 }} />
                                                </Avatar>
                                                <Typography variant="h6" gutterBottom>Drop your ToR here</Typography>
                                                <Typography variant="body2" color="text.secondary">or click to browse • PDF files up to 20MB</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {selectedFile && (
                                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                                            <Button variant="contained" size="large" 
                                                startIcon={isExtracting ? <CircularProgress size={20} color="inherit" /> : <DescriptionIcon />}
                                                onClick={handleExtractRequirements}
                                                disabled={isExtracting}
                                            >
                                                {isExtracting ? 'Analysing ToR...' : 'Analyse ToR'}
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                            
                            <Typography variant="caption" display="block" sx={{ mt: 3, textAlign: 'center' }} color="text.secondary">
                                {inputMode === 'upload' ? 'Upload your ToR to generate methodology' : 'Select a ToR to view its methodology'}
                            </Typography>
                        </Paper>
                    </Zoom>
                )}
                
                {/* Requirements Review Section - Step 2 */}
                {!isProcessing && !result && torRequirements && (
                    <Fade in timeout={300}>
                        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                            {/* File info header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}><CheckCircleIcon /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={600}>Requirements Extracted</Typography>
                                        <Typography variant="body2" color="text.secondary">{selectedFile?.name}</Typography>
                                    </Box>
                                </Box>
                                <Button variant="outlined" size="small" onClick={handleReset} startIcon={<RefreshIcon />}>
                                    Start Over
                                </Button>
                            </Box>
                            
                            <Divider sx={{ mb: 3 }} />
                            
                            {/* Customize Requirements - Only show when CV Search is enabled */}
                            {enableCVSearch && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            <TuneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Customise Requirements for CV Search
                                        </Typography>
                                        <Button 
                                            variant={showCustomize ? 'contained' : 'outlined'} 
                                            size="small"
                                            onClick={() => setShowCustomize(!showCustomize)}
                                            startIcon={<TuneIcon />}>
                                            {showCustomize ? 'Hide' : 'Customise'}
                                        </Button>
                                    </Box>
                                    
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {showCustomize 
                                            ? 'Click chips to cycle tier (Required → Preferred → Bonus). Right-click to remove. Add new items below each category.'
                                            : 'All extracted requirements will be used as "Required". Click Customise to adjust.'}
                                    </Typography>
                                    
                                    {/* Legend */}
                                    {showCustomize && (
                                        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography variant="caption" color="text.secondary">Click to cycle:</Typography>
                                            <Chip label="Required" size="small" sx={{ bgcolor: '#b71c1c', color: 'white' }} />
                                            <Typography variant="caption">→</Typography>
                                            <Chip label="Preferred" size="small" sx={{ bgcolor: '#e65100', color: 'white' }} />
                                            <Typography variant="caption">→</Typography>
                                            <Chip label="Bonus" size="small" sx={{ bgcolor: '#2e7d32', color: 'white' }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>| Right-click to remove</Typography>
                                        </Box>
                                    )}
                                    
                                    {/* Collapsible requirements editor - V2 with per-item tiers */}
                                    <Collapse in={showCustomize}>
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            {renderTieredCategory('geographic_countries', 'Geographic Countries', 'Add country...')}
                                            {renderTieredCategory('geographic_regions', 'Geographic Regions', 'Add region...')}
                                            {renderTieredCategory('research_evaluation', 'Methods & Methodologies', 'Add method...')}
                                            {renderTieredCategory('technical_domain', 'Technical & Domain Expertise', 'Add expertise...')}
                                            {renderTieredCategory('analytical_tools', 'Technical Tools & Software', 'Add tool...')}
                                            {renderTieredCategory('frameworks', 'Frameworks & Standards', 'Add framework...')}
                                            {renderTieredCategory('communication', 'Communication & Stakeholder', 'Add skill...')}
                                            {renderTieredCategory('professional_ethical', 'Professional & Ethical', 'Add standard...')}
                                            {renderTieredCategory('languages', 'Languages', 'Add language...')}
                                            {renderTieredCategory('sectors', 'Sector Experience', 'Add sector...')}
                                            {renderTieredCategory('donor_experience', 'Donor Experience', 'Add donor...')}
                                            {renderTieredCategory('education', 'Education Level', 'Add education...')}
                                            {renderTieredCategory('experience_years', 'Years of Experience', 'Add experience...')}
                                        </Grid>
                                    </Collapse>
                                    
                                    <Divider sx={{ mb: 3 }} />
                                </>
                            )}
                            
                            {/* Generate button */}
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                                <Button variant="contained" size="large" 
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handleStartProcessing}>
                                    {enableCVSearch ? 'Generate Methodology & Search CVs' : 'Generate Methodology'}
                                </Button>
                                <Typography variant="caption" display="block" sx={{ mt: 2 }} color="text.secondary">
                                    {enableCVSearch 
                                        ? 'This will generate your methodology and search for matching consultants'
                                        : 'This will generate your methodology based on the ToR'}
                                </Typography>
                            </Box>
                        </Paper>
                    </Fade>
                )}
                
                {/* Progress Section - only show during processing, not after completion */}
                {isProcessing && progress && (
                    <Fade in timeout={500}>
                        <Box sx={{ mb: 4 }}>
                            {isProcessing && (
                                <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <CircularProgress variant="determinate" value={overallProgress} size={48} sx={{ mr: 2 }} />
                                        <Box>
                                            <Typography variant="h6" fontWeight={600}>Processing Your ToR</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {Math.round(overallProgress)}% complete
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <LinearProgress variant="determinate" value={overallProgress} sx={{ height: 10, borderRadius: 5 }} />
                                </Paper>
                            )}
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <ProgressCard title="ToR Analysis" icon={<DescriptionIcon />} state={progress.torExtraction} color="#2196f3" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ProgressCard 
                                        title="Methodology AI" 
                                        icon={<PsychologyIcon />} 
                                        state={progress.methodology} 
                                        color="#9c27b0"
                                        onRetry={handleRetryMethodology}
                                        isRetrying={isRetryingMethodology}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Fade>
                )}
                
                {/* Results Section */}
                {result && isComplete && (
                    <Fade in timeout={500}>
                        <Box>
                            {/* Success Banner */}
                            <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2, bgcolor: 'background.paper' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                        <Box>
                                            <Typography variant="h6" fontWeight={600}>Proposal Package Ready!</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {result.methodology?.alignmentScore && (
                                                    <>
                                                        Methodology Score: {result.methodology.alignmentScore}%
                                                        <Tooltip 
                                                            title="Measures how well the generated methodology aligns with the ToR requirements. Higher scores indicate better coverage of key themes, deliverables, and evaluation criteria from the original terms of reference."
                                                            arrow
                                                            placement="right"
                                                        >
                                                            <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Stack direction="row" spacing={2}>
                                        <Button 
                                            variant="contained" 
                                            color="warning"
                                            startIcon={<GroupsIcon />} 
                                            onClick={handleOpenCVSearch}
                                        >
                                            Find Matching CVs
                                        </Button>
                                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleReset}>New ToR</Button>
                                    </Stack>
                                </Box>
                            </Paper>
                            
                            {/* Tab Navigation */}
                            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
                                    {[
                                        { key: 'overview', label: 'Overview', icon: <DescriptionIcon />, badge: null, color: 'success' },
                                        { key: 'methodology', label: 'Methodology Guidance', icon: <PsychologyIcon />, badge: result.methodology?.alignmentScore ? `${result.methodology.alignmentScore}%` : null, color: 'primary' },
                                        { key: 'costs', label: 'Indicative Costs', icon: <CalculateIcon />, badge: null, color: 'info' },
                                        { key: 'sources', label: 'Sources & Questions', icon: <SourceIcon />, badge: (result.methodology?.sourceProposals?.length || 0) + (result.methodology?.evaluationQuestions?.length || 0), color: 'secondary' }
                                    ].map(tab => (
                                        <Button key={tab.key}
                                            sx={{ flex: 1, py: 2, borderRadius: 0,
                                                borderBottom: activeTab === tab.key ? 3 : 0,
                                                borderColor: `${tab.color}.main`,
                                                bgcolor: activeTab === tab.key ? `${tab.color}.50` : 'transparent' }}
                                            startIcon={tab.icon}
                                            onClick={() => setActiveTab(tab.key as any)}>
                                            <Badge badgeContent={tab.badge} color={tab.color as any}>{tab.label}</Badge>
                                        </Button>
                                    ))}
                                </Box>
                            </Paper>
                            
                            {/* Overview Tab */}
                            {activeTab === 'overview' && result.methodology && (
                                <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                                    <Typography variant="h5" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AssessmentIcon color="success" />
                                        Project Overview
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />
                                    
                                    <Grid container spacing={3}>
                                        {/* Project Info Card */}
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined" sx={{ height: '100%' }}>
                                                <CardContent>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <DescriptionIcon fontSize="small" />
                                                        PROJECT DETAILS
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                                        {result.methodology.projectTitle || 'Untitled Project'}
                                                    </Typography>
                                                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="body2" color="text.secondary">Client</Typography>
                                                            <Typography variant="body2" fontWeight={500}>{result.methodology.clientName || 'Not specified'}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mr: 1 }}>Evaluation Type</Typography>
                                                            <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right', wordBreak: 'break-word' }}>{result.methodology.evaluationType || 'Standard'}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Typography variant="body2" color="text.secondary">Timeline Pressure</Typography>
                                                            <Chip 
                                                                label={result.methodology.timelinePressure || 'Normal'} 
                                                                size="small" 
                                                                color={result.methodology.timelinePressure === 'high' ? 'error' : result.methodology.timelinePressure === 'low' ? 'success' : 'default'}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Geographic Scope Card */}
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined" sx={{ height: '100%' }}>
                                                <CardContent>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PublicIcon fontSize="small" />
                                                        GEOGRAPHIC SCOPE
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                                        <Typography variant="h3" fontWeight={700} color="primary.main">
                                                            {result.methodology.numberOfCountries || 1}
                                                        </Typography>
                                                        <Typography variant="body1" color="text.secondary">
                                                            {(result.methodology.numberOfCountries || 1) === 1 ? 'Country' : 'Countries'}
                                                        </Typography>
                                                    </Box>
                                                    {result.methodology.mentionedCountries && result.methodology.mentionedCountries.length > 0 && (
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2, gap: 1 }}>
                                                            {result.methodology.mentionedCountries.map((country: string, idx: number) => (
                                                                <Chip 
                                                                    key={idx} 
                                                                    label={country} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        bgcolor: 'primary.50', 
                                                                        borderColor: 'primary.200',
                                                                        color: 'primary.main',
                                                                        '& .MuiChip-icon': { color: 'primary.main' }
                                                                    }} 
                                                                    icon={<PublicIcon />} 
                                                                />
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Alignment Score Card */}
                                        <Grid item xs={12} md={4}>
                                            <Card variant="outlined" sx={{ height: '100%', bgcolor: result.methodology.alignmentScore >= 80 ? 'success.50' : 'warning.50' }}>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        ALIGNMENT SCORE
                                                    </Typography>
                                                    <Typography variant="h2" fontWeight={700} color={result.methodology.alignmentScore >= 80 ? 'success.main' : 'warning.main'}>
                                                        {result.methodology.alignmentScore}%
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                        {result.methodology.iterations} QA iteration{result.methodology.iterations > 1 ? 's' : ''}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Budget Card */}
                                        <Grid item xs={12} md={4}>
                                            <Card variant="outlined" sx={{ height: '100%' }}>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                        {result.methodology.budgetCurrency || 'GBP'} BUDGET
                                                    </Typography>
                                                    <Typography variant="h4" fontWeight={700}>
                                                        {({'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF ', 'JPY': '¥', 'CNY': '¥', 'INR': '₹', 'CAD': 'C$', 'AUD': 'A$'}[result.methodology.budgetCurrency || 'GBP'] || (result.methodology.budgetCurrency + ' '))}{((result.methodology.costBreakdown?.total_central || 0) / 1000).toFixed(0)}k
                                                    </Typography>
                                                    {(() => {
                                                        const total = result.methodology.costBreakdown?.total_central || 0;
                                                        const band = total < 100000 ? 'Small' : total <= 600000 ? 'Medium' : 'Large';
                                                        const color = band === 'Small' ? 'success' : band === 'Large' ? 'error' : 'warning';
                                                        return <Chip label={band} size="small" color={color} sx={{ mt: 1 }} />;
                                                    })()}
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Level of Effort Card */}
                                        <Grid item xs={12} md={4}>
                                            <Card variant="outlined" sx={{ height: '100%' }}>
                                                <CardContent sx={{ textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                        <ScheduleIcon fontSize="small" />
                                                        LEVEL OF EFFORT
                                                    </Typography>
                                                    <Typography variant="h4" fontWeight={700}>
                                                        {result.methodology.estimatedDays || 0}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Total Days
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        {/* Team Composition */}
                                        {result.methodology.roleDays && (
                                            <Grid item xs={12}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <GroupsIcon fontSize="small" />
                                                            TEAM COMPOSITION
                                                        </Typography>
                                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                                            {Object.entries(result.methodology.roleDays).map(([role, days]) => (
                                                                <Grid item xs={6} sm={3} key={role}>
                                                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                                                        <Typography variant="h5" fontWeight={600}>{days}</Typography>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                                                                            {role.replace('_', ' ')} days
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )}

                                        {/* Phase Breakdown */}
                                        {result.methodology.daysBreakdown && (
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined" sx={{ height: '100%' }}>
                                                    <CardContent>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AssessmentIcon fontSize="small" />
                                                            PHASE BREAKDOWN
                                                        </Typography>
                                                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                                                            {[
                                                                { label: 'Inception & Design', value: result.methodology.daysBreakdown.inception || 0, color: '#4caf50' },
                                                                { label: 'Data Collection', value: result.methodology.daysBreakdown.data_collection || 0, color: '#2196f3' },
                                                                { label: 'Analysis & Synthesis', value: result.methodology.daysBreakdown.analysis || 0, color: '#ff9800' },
                                                                { label: 'Reporting & Dissemination', value: result.methodology.daysBreakdown.reporting || 0, color: '#9c27b0' }
                                                            ].map((phase) => {
                                                                // Calculate percentage against sum of all phases, not total LOE
                                                                const db = result.methodology?.daysBreakdown;
                                                                const phaseTotal = (db?.inception || 0) + (db?.data_collection || 0) + (db?.analysis || 0) + (db?.reporting || 0) || 1;
                                                                const percent = Math.round((phase.value / phaseTotal) * 100);
                                                                return (
                                                                    <Box key={phase.label}>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                            <Typography variant="body2">{phase.label}</Typography>
                                                                            <Typography variant="body2" fontWeight={600}>{phase.value} days ({percent}%)</Typography>
                                                                        </Box>
                                                                        <LinearProgress 
                                                                            variant="determinate" 
                                                                            value={percent} 
                                                                            sx={{ 
                                                                                height: 8, 
                                                                                borderRadius: 4,
                                                                                bgcolor: 'action.hover',
                                                                                '& .MuiLinearProgress-bar': { bgcolor: phase.color, borderRadius: 4 }
                                                                            }} 
                                                                        />
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )}

                                        {/* Budget Alignment */}
                                        {(result.methodology.budgetCeiling || result.methodology.costBreakdown) && (
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined" sx={{ height: '100%' }}>
                                                    <CardContent>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <AttachMoneyIcon fontSize="small" />
                                                            BUDGET ALIGNMENT
                                                        </Typography>
                                                        <Stack spacing={2} sx={{ mt: 2 }}>
                                                            {result.methodology.budgetCeiling && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">Client Budget Ceiling</Typography>
                                                                    <Typography variant="body1" fontWeight={600}>
                                                                        {result.methodology.budgetCurrency || 'GBP'} {result.methodology.budgetCeiling.toLocaleString()}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {result.methodology.costBreakdown && (
                                                                <>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Typography variant="body2" color="text.secondary">Estimated Range</Typography>
                                                                        <Typography variant="body1" fontWeight={500}>
                                                                            {result.methodology.budgetCurrency || 'GBP'} {(result.methodology.costBreakdown.total_low || 0).toLocaleString()} - {(result.methodology.costBreakdown.total_high || 0).toLocaleString()}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Typography variant="body2" color="text.secondary">Central Estimate</Typography>
                                                                        <Typography variant="h6" fontWeight={700} color="primary.main">
                                                                            {result.methodology.budgetCurrency || 'GBP'} {(result.methodology.costBreakdown.total_central || 0).toLocaleString()}
                                                                        </Typography>
                                                                    </Box>
                                                                </>
                                                            )}
                                                            {result.methodology.budgetFit && (
                                                                <Box sx={{ 
                                                                    p: 1.5, 
                                                                    borderRadius: 2, 
                                                                    bgcolor: result.methodology.budgetFit === 'within' ? 'success.50' : result.methodology.budgetFit === 'over' ? 'error.50' : 'warning.50',
                                                                    border: '1px solid',
                                                                    borderColor: result.methodology.budgetFit === 'within' ? 'success.200' : result.methodology.budgetFit === 'over' ? 'error.200' : 'warning.200'
                                                                }}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {result.methodology.budgetFit === 'within' ? '✓ Within Budget' : result.methodology.budgetFit === 'over' ? '⚠ Over Budget' : 'Budget Status'}
                                                                        </Typography>
                                                                        {result.methodology.budgetGap !== undefined && result.methodology.budgetGap !== null && (
                                                                            <Typography variant="body2" fontWeight={600} color={result.methodology.budgetFit === 'within' ? 'success.main' : 'error.main'}>
                                                                                {result.methodology.budgetFit === 'within' ? 'Under by ' : 'Over by '}
                                                                                {result.methodology.budgetCurrency || 'GBP'} {Math.abs(result.methodology.budgetGap).toLocaleString()}
                                                                                {result.methodology.budgetGapPercent ? ` (${Math.abs(result.methodology.budgetGapPercent)}%)` : ''}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                            {result.methodology.estimationConfidence && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">Estimation Confidence</Typography>
                                                                    <Chip 
                                                                        label={result.methodology.estimationConfidence} 
                                                                        size="small"
                                                                        color={result.methodology.estimationConfidence === 'high' ? 'success' : result.methodology.estimationConfidence === 'low' ? 'warning' : 'default'}
                                                                        variant="outlined"
                                                                        sx={{ textTransform: 'capitalize' }}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )}

                                        {/* Source Proposals */}
                                        {result.methodology.sourceProposals && result.methodology.sourceProposals.length > 0 && (
                                            <Grid item xs={12}>
                                                <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                                                    <CardContent>
                                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                            <SourceIcon fontSize="small" />
                                                            SIMILAR PROPOSALS REFERENCED
                                                        </Typography>
                                                        <Grid container spacing={1}>
                                                            {result.methodology.sourceProposals.map((proposal, idx) => {
                                                                // Clean up proposal name: remove "P1234 - S - " prefix and any brackets/quotes
                                                                const cleanName = proposal
                                                                    .replace(/^P\d+\s*-\s*S\s*-\s*/i, '')
                                                                    .replace(/[\[\]"']/g, '')
                                                                    .trim();
                                                                return (
                                                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                                                        <Box sx={{ 
                                                                            p: 1.5, 
                                                                            bgcolor: 'secondary.50', 
                                                                            borderRadius: 1,
                                                                            borderLeft: '3px solid',
                                                                            borderColor: 'secondary.main',
                                                                            height: '100%'
                                                                        }}>
                                                                            <Typography variant="body2" fontWeight={500} color="secondary.dark" sx={{ lineHeight: 1.4 }}>
                                                                                {cleanName}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                );
                                                            })}
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>
                            )}
                            
                            {/* Methodology Tab */}
                            {activeTab === 'methodology' && result.methodology && (
                                <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                        <Box>
                                            <Typography variant="h5" fontWeight={600} gutterBottom>AI-Generated Methodology Guidance</Typography>
                                            <Stack direction="row" spacing={1}>
                                                <Chip icon={<TrendingUpIcon />} label={`Alignment: ${result.methodology.alignmentScore}%`}
                                                    color={result.methodology.alignmentScore >= 80 ? 'success' : 'warning'} size="small" />
                                                <Chip label={`${result.methodology.wordCount} words`} size="small" variant="outlined" />
                                                <Chip label={`${result.methodology.iterations} iteration${result.methodology.iterations > 1 ? 's' : ''}`} size="small" variant="outlined" />
                                            </Stack>
                                        </Box>
                                        <Tooltip title={copiedMethodology ? 'Copied!' : 'Copy to clipboard'}>
                                            <IconButton onClick={handleCopyMethodology} color="primary"><ContentCopyIcon /></IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Divider sx={{ mb: 3 }} />
                                    <Box sx={{ 
                                        maxHeight: 600, 
                                        overflow: 'auto', 
                                        p: 3, 
                                        bgcolor: 'background.default', 
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        '& h1, & h2, & h3': { 
                                            color: 'text.primary', 
                                            fontWeight: 600,
                                            mt: 3,
                                            mb: 1.5,
                                            '&:first-of-type': { mt: 0 }
                                        },
                                        '& h1': { fontSize: '1.5rem', borderBottom: '1px solid', borderColor: 'divider', pb: 1 },
                                        '& h2': { fontSize: '1.25rem', color: 'text.secondary' },
                                        '& h3': { fontSize: '1.1rem' },
                                        '& p': { mb: 1.5, lineHeight: 1.7, color: 'text.secondary' },
                                        '& ul, & ol': { pl: 3, mb: 2 },
                                        '& li': { mb: 0.5, lineHeight: 1.6, color: 'text.secondary' },
                                        '& strong': { fontWeight: 600, color: 'text.primary' }
                                    }}>
                                        <ReactMarkdown>
                                            {(result.methodology.generatedText || 'No methodology text available')
                                                // Remove (see "field_name") references - they're internal metadata
                                                .replace(/\s*\(see\s*["'][\w_]+["']\)/gi, '')
                                                // Clean up invalid/corrupted characters
                                                .replace(/\?\?/g, '✓')  // Replace ?? with checkmark for Strength
                                                .replace(/\?(?=\s*Priority)/g, '⚠')  // Replace ? before Priority with warning
                                                .replace(/\u0027\u0027/g, "'")  // Fix double-encoded apostrophes
                                                .replace(/\u002B/g, '+')  // Fix encoded plus signs
                                                // Format numbered sections as headers
                                                .replace(/^(\d+)\.\s+/gm, '## $1. ')
                                            }
                                        </ReactMarkdown>
                                    </Box>
                                    {/* QA info banner - QA feedback is now merged into the main methodology text */}
                                    {result.methodology.iterations && result.methodology.iterations > 1 && (
                                        <Alert severity="success" sx={{ mt: 2 }}>
                                            <strong>QA Validated:</strong> This guidance was refined through {result.methodology.iterations} iteration(s) to achieve {result.methodology.alignmentScore}% alignment with ToR requirements.
                                        </Alert>
                                    )}
                                </Paper>
                            )}
                            
                            {/* Indicative Costs Tab */}
                            {activeTab === 'costs' && (
                                <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                        <Box>
                                            <Typography variant="h5" fontWeight={600} gutterBottom>
                                                <CalculateIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'info.main' }} />
                                                Indicative Cost Calculator
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Adjust days and costs below. This is a planning tool - changes are not saved.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Tooltip title={
                                                ratesLastUpdated 
                                                    ? `Rates auto-convert. Updated: ${ratesLastUpdated.toLocaleTimeString()}`
                                                    : 'Rates auto-convert on currency change'
                                            }>
                                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                                    <Select
                                                        value={selectedCurrency}
                                                        onChange={(e) => handleCurrencyChange(e.target.value)}
                                                        disabled={isLoadingRates}
                                                    >
                                                        {CURRENCY_OPTIONS.map(opt => (
                                                            <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Tooltip>
                                            <Tooltip title={copiedCosts ? 'Copied!' : 'Copy to clipboard'}>
                                                <IconButton onClick={handleCopyCosts} color="primary"><ContentCopyIcon /></IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    
                                    {/* Investment Band - calculated from total */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Chip 
                                            label={`Investment Band: ${getInvestmentBand(calculateGrandTotal())}`} 
                                            color={getInvestmentBand(calculateGrandTotal()) === 'Small' ? 'success' : getInvestmentBand(calculateGrandTotal()) === 'Medium' ? 'warning' : 'error'}
                                        />
                                        {result.methodology?.estimatedDays && (
                                            <Chip 
                                                label={`Estimated Total: ${result.methodology.estimatedDays} days`} 
                                                variant="outlined"
                                                color="info"
                                            />
                                        )}
                                    </Box>
                                    
                                    {/* LOE Explanation Expander */}
                                    {result.methodology?.loeExplanation && (() => {
                                        // Parse JSON if string, otherwise use as-is
                                        let loeData: any = null;
                                        try {
                                            loeData = typeof result.methodology.loeExplanation === 'string' 
                                                ? JSON.parse(result.methodology.loeExplanation) 
                                                : result.methodology.loeExplanation;
                                        } catch (e) {
                                            console.warn('Failed to parse loeExplanation as JSON');
                                        }
                                        
                                        if (!loeData?.summary) return null;
                                        
                                        return (
                                            <Accordion 
                                                defaultExpanded={false}
                                                sx={{ 
                                                    mb: 3, 
                                                    bgcolor: 'background.paper',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    '&:before': { display: 'none' },
                                                    borderRadius: '8px !important',
                                                    '&.Mui-expanded': { margin: '0 0 24px 0' }
                                                }}
                                            >
                                                <AccordionSummary 
                                                    expandIcon={<ExpandMoreIcon />}
                                                    sx={{ 
                                                        bgcolor: 'info.50',
                                                        borderRadius: '8px 8px 0 0',
                                                        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 }
                                                    }}
                                                >
                                                    <CalculateIcon fontSize="small" color="info" />
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        How These Costs Were Calculated
                                                    </Typography>
                                                    <Tooltip title={
                                                        loeData.summary.confidence === 'high' 
                                                            ? 'High confidence: Clear scope, established patterns, and reliable data inputs'
                                                            : loeData.summary.confidence === 'medium'
                                                            ? 'Medium confidence: Some complexity or uncertainty in scope, timeline, or methodology'
                                                            : 'Low confidence: Significant unknowns or complexity requiring careful review'
                                                    }>
                                                        <Chip 
                                                            label={`${loeData.summary.confidence} confidence`} 
                                                            size="small" 
                                                            color={loeData.summary.confidence === 'high' ? 'success' : loeData.summary.confidence === 'medium' ? 'warning' : 'error'}
                                                            sx={{ ml: 'auto', mr: 1, cursor: 'help' }}
                                                            icon={<HelpOutlineIcon sx={{ fontSize: '1rem !important' }} />}
                                                        />
                                                    </Tooltip>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 3 }}>
                                                    {/* Summary Header */}
                                                    <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                                        <Typography variant="body1" fontWeight={500} gutterBottom>
                                                            {loeData.summary.headline}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                                            <Chip label={`${loeData.summary.total_days} total days`} size="small" color="primary" />
                                                            <Chip label={loeData.summary.investment_band} size="small" variant="outlined" />
                                                        </Box>
                                                        {loeData.summary.confidence_reason && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                                {loeData.summary.confidence_reason}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    {/* Key Cost Drivers */}
                                                    {loeData.key_drivers?.length > 0 && (
                                                        <Box sx={{ mb: 3 }}>
                                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                Key Cost Drivers
                                                            </Typography>
                                                            <Stack spacing={1}>
                                                                {loeData.key_drivers.map((driver: any, idx: number) => (
                                                                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                                        <Box>
                                                                            <Typography variant="body2" fontWeight={500}>{driver.driver}</Typography>
                                                                            <Typography variant="caption" color="text.secondary">{driver.impact}</Typography>
                                                                        </Box>
                                                                        <Chip label={`+${driver.days_attributed} days`} size="small" color="warning" variant="outlined" />
                                                                    </Box>
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    )}

                                                    {/* Phase Breakdown */}
                                                    {loeData.phase_rationale && (
                                                        <Box sx={{ mb: 3 }}>
                                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                Phase Breakdown
                                                            </Typography>
                                                            <Grid container spacing={2}>
                                                                {Object.entries(loeData.phase_rationale).map(([phase, data]: [string, any]) => (
                                                                    <Grid item xs={12} sm={6} key={phase}>
                                                                        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                                <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
                                                                                    {phase.replace('_', ' ')}
                                                                                </Typography>
                                                                                <Typography variant="body2" fontWeight={600} color="primary.main">
                                                                                    {data.days} days ({data.percentage}%)
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {data.rationale}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Box>
                                                    )}

                                                    {/* Team Allocation */}
                                                    {loeData.team_rationale?.length > 0 && (
                                                        <Box sx={{ mb: 3 }}>
                                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                Team Allocation Rationale
                                                            </Typography>
                                                            <TableContainer>
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell><strong>Role</strong></TableCell>
                                                                            <TableCell align="center"><strong>Days</strong></TableCell>
                                                                            <TableCell><strong>Focus Areas</strong></TableCell>
                                                                            <TableCell><strong>Rationale</strong></TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {loeData.team_rationale.map((team: any, idx: number) => (
                                                                            <TableRow key={idx}>
                                                                                <TableCell><strong>{team.role}</strong></TableCell>
                                                                                <TableCell align="center">{team.days} ({team.percentage}%)</TableCell>
                                                                                <TableCell>
                                                                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                                                        {team.focus_areas?.map((area: string, i: number) => (
                                                                                            <Chip key={i} label={area} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                                                                        ))}
                                                                                    </Stack>
                                                                                </TableCell>
                                                                                <TableCell><Typography variant="caption">{team.rationale}</Typography></TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </TableContainer>
                                                        </Box>
                                                    )}

                                                    {/* Adjustments Applied */}
                                                    {loeData.adjustments_applied?.length > 0 && (
                                                        <Box sx={{ mb: 3 }}>
                                                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                Adjustments Applied
                                                            </Typography>
                                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                                {loeData.adjustments_applied.map((adj: any, idx: number) => (
                                                                    <Tooltip key={idx} title={adj.reason}>
                                                                        <Chip 
                                                                            label={`${adj.adjustment}: ${adj.percentage_change}`} 
                                                                            size="small" 
                                                                            color="warning"
                                                                        />
                                                                    </Tooltip>
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    )}

                                                    {/* Assumptions & Risks */}
                                                    <Grid container spacing={2}>
                                                        {loeData.assumptions?.length > 0 && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                    Key Assumptions
                                                                </Typography>
                                                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                                    {loeData.assumptions.map((assumption: string, idx: number) => (
                                                                        <Typography component="li" variant="caption" color="text.secondary" key={idx} sx={{ mb: 0.5 }}>
                                                                            {assumption}
                                                                        </Typography>
                                                                    ))}
                                                                </Box>
                                                            </Grid>
                                                        )}
                                                        {loeData.risks?.length > 0 && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                    Risk Factors
                                                                </Typography>
                                                                <Stack spacing={0.5}>
                                                                    {loeData.risks.map((risk: any, idx: number) => (
                                                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Chip 
                                                                                label={risk.impact} 
                                                                                size="small" 
                                                                                color={risk.impact === 'high' ? 'error' : risk.impact === 'medium' ? 'warning' : 'success'}
                                                                                sx={{ minWidth: 60, fontSize: '0.7rem' }}
                                                                            />
                                                                            <Tooltip title={risk.mitigation}>
                                                                                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                                                                                    {risk.risk}
                                                                                </Typography>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    ))}
                                                                </Stack>
                                                            </Grid>
                                                        )}
                                                    </Grid>

                                                    {/* Notes */}
                                                    {loeData.notes?.length > 0 && (
                                                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                <strong>Notes:</strong> {loeData.notes.join(' • ')}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Cost Rationale Section */}
                                                    {loeData.cost_rationale && (
                                                        <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                                                            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <AttachMoneyIcon fontSize="small" color="success" />
                                                                Cost Breakdown Details
                                                            </Typography>

                                                            {/* Budget Summary */}
                                                            {loeData.cost_rationale.summary && (
                                                                <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
                                                                    <Grid container spacing={2} alignItems="center">
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography variant="caption" color="text.secondary">Total Range</Typography>
                                                                            <Typography variant="h6" fontWeight={600}>
                                                                                {loeData.cost_rationale.summary.total_range}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography variant="caption" color="text.secondary">Central Estimate</Typography>
                                                                            <Typography variant="h5" fontWeight={700} color="success.main">
                                                                                {loeData.cost_rationale.summary.central_estimate}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography variant="caption" color="text.secondary">Currency</Typography>
                                                                            <Typography variant="body1" fontWeight={500}>
                                                                                {loeData.cost_rationale.summary.currency}
                                                                            </Typography>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Box>
                                                            )}

                                                            {/* Consultant Fees */}
                                                            {loeData.cost_rationale.consultant_fees && (
                                                                <Box sx={{ mb: 3 }}>
                                                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                        Consultant Fees: {loeData.cost_rationale.consultant_fees.total}
                                                                    </Typography>
                                                                    <TableContainer>
                                                                        <Table size="small">
                                                                            <TableHead>
                                                                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                                                    <TableCell><strong>Role</strong></TableCell>
                                                                                    <TableCell align="center"><strong>Days</strong></TableCell>
                                                                                    <TableCell align="right"><strong>Rate</strong></TableCell>
                                                                                    <TableCell align="right"><strong>Total</strong></TableCell>
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {loeData.cost_rationale.consultant_fees.breakdown?.map((fee: any, idx: number) => (
                                                                                    <TableRow key={idx}>
                                                                                        <TableCell>{fee.role}</TableCell>
                                                                                        <TableCell align="center">{fee.days}</TableCell>
                                                                                        <TableCell align="right">{fee.rate}</TableCell>
                                                                                        <TableCell align="right"><strong>{fee.total}</strong></TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    </TableContainer>
                                                                    {loeData.cost_rationale.consultant_fees.note && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                                            {loeData.cost_rationale.consultant_fees.note}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}

                                                            {/* Travel & Accommodation */}
                                                            {loeData.cost_rationale.travel_and_accommodation && (
                                                                <Box sx={{ mb: 3 }}>
                                                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                        Travel & Accommodation: {loeData.cost_rationale.travel_and_accommodation.total}
                                                                    </Typography>
                                                                    {loeData.cost_rationale.travel_and_accommodation.components?.length > 0 && (
                                                                        <Stack spacing={1} sx={{ mb: 1 }}>
                                                                            {loeData.cost_rationale.travel_and_accommodation.components.map((comp: any, idx: number) => (
                                                                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                                                    <Box>
                                                                                        <Typography variant="body2">{comp.item}</Typography>
                                                                                        <Typography variant="caption" color="text.secondary">{comp.calculation}</Typography>
                                                                                    </Box>
                                                                                    <Typography variant="body2" fontWeight={600}>{comp.total}</Typography>
                                                                                </Box>
                                                                            ))}
                                                                        </Stack>
                                                                    )}
                                                                    {loeData.cost_rationale.travel_and_accommodation.assumptions?.length > 0 && (
                                                                        <Box sx={{ mt: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                <strong>Assumptions:</strong> {loeData.cost_rationale.travel_and_accommodation.assumptions.join(' • ')}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            )}

                                                            {/* Sub-contractors */}
                                                            {loeData.cost_rationale.sub_contractors && loeData.cost_rationale.sub_contractors.total_raw > 0 && (
                                                                <Box sx={{ mb: 3 }}>
                                                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                        Sub-contractors: {loeData.cost_rationale.sub_contractors.total}
                                                                    </Typography>
                                                                    {loeData.cost_rationale.sub_contractors.items?.map((item: any, idx: number) => (
                                                                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                                                                            <Box>
                                                                                <Typography variant="body2">{item.item}</Typography>
                                                                                <Typography variant="caption" color="text.secondary">{item.calculation}</Typography>
                                                                                {item.rationale && (
                                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                        {item.rationale}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                            <Typography variant="body2" fontWeight={600}>{item.total}</Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            )}

                                                            {/* Overhead & Contingency */}
                                                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                                                {loeData.cost_rationale.overhead && (
                                                                    <Grid item xs={12} md={6}>
                                                                        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="subtitle2" fontWeight={600}>
                                                                                    Overhead ({loeData.cost_rationale.overhead.rate})
                                                                                </Typography>
                                                                                <Typography variant="h6" fontWeight={600} color="primary.main">
                                                                                    {loeData.cost_rationale.overhead.total}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                                {loeData.cost_rationale.overhead.basis}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                                Base: {loeData.cost_rationale.overhead.base_amount} | Calculation: {loeData.cost_rationale.overhead.calculation}
                                                                            </Typography>
                                                                            {loeData.cost_rationale.overhead.includes?.length > 0 && (
                                                                                <Box sx={{ mt: 1 }}>
                                                                                    <Typography variant="caption" color="text.secondary"><strong>Includes:</strong></Typography>
                                                                                    <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                                                                                        {loeData.cost_rationale.overhead.includes.map((item: string, idx: number) => (
                                                                                            <Typography component="li" variant="caption" color="text.secondary" key={idx}>
                                                                                                {item}
                                                                                            </Typography>
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                )}
                                                                {loeData.cost_rationale.contingency && (
                                                                    <Grid item xs={12} md={6}>
                                                                        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="subtitle2" fontWeight={600}>
                                                                                    Contingency ({loeData.cost_rationale.contingency.rate})
                                                                                </Typography>
                                                                                <Typography variant="h6" fontWeight={600} color="warning.main">
                                                                                    {loeData.cost_rationale.contingency.total}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                                {loeData.cost_rationale.contingency.rationale}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                                Base: {loeData.cost_rationale.contingency.base_amount} | Calculation: {loeData.cost_rationale.contingency.calculation}
                                                                            </Typography>
                                                                            {loeData.cost_rationale.contingency.covers?.length > 0 && (
                                                                                <Box sx={{ mt: 1 }}>
                                                                                    <Typography variant="caption" color="text.secondary"><strong>Covers:</strong></Typography>
                                                                                    <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                                                                                        {loeData.cost_rationale.contingency.covers.map((item: string, idx: number) => (
                                                                                            <Typography component="li" variant="caption" color="text.secondary" key={idx}>
                                                                                                {item}
                                                                                            </Typography>
                                                                                        ))}
                                                                                    </Box>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                )}
                                                            </Grid>

                                                            {/* Budget Range */}
                                                            {loeData.cost_rationale.budget_range && (
                                                                <Box sx={{ mb: 3 }}>
                                                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                        Budget Scenarios
                                                                    </Typography>
                                                                    <Grid container spacing={2}>
                                                                        {loeData.cost_rationale.budget_range.low && (
                                                                            <Grid item xs={12} md={4}>
                                                                                <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, textAlign: 'center' }}>
                                                                                    <Typography variant="caption" color="text.secondary">Best Case</Typography>
                                                                                    <Typography variant="h6" fontWeight={600} color="success.main">
                                                                                        {loeData.cost_rationale.budget_range.low.value}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {loeData.cost_rationale.budget_range.low.scenario}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>
                                                                        )}
                                                                        {loeData.cost_rationale.budget_range.central && (
                                                                            <Grid item xs={12} md={4}>
                                                                                <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, textAlign: 'center', border: '2px solid', borderColor: 'primary.main' }}>
                                                                                    <Typography variant="caption" color="text.secondary">Expected Case</Typography>
                                                                                    <Typography variant="h6" fontWeight={700} color="primary.main">
                                                                                        {loeData.cost_rationale.budget_range.central.value}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {loeData.cost_rationale.budget_range.central.scenario}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>
                                                                        )}
                                                                        {loeData.cost_rationale.budget_range.high && (
                                                                            <Grid item xs={12} md={4}>
                                                                                <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, textAlign: 'center' }}>
                                                                                    <Typography variant="caption" color="text.secondary">Contingency Case</Typography>
                                                                                    <Typography variant="h6" fontWeight={600} color="warning.main">
                                                                                        {loeData.cost_rationale.budget_range.high.value}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" color="text.secondary">
                                                                                        {loeData.cost_rationale.budget_range.high.scenario}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Box>
                                                            )}

                                                            {/* Calculation Method */}
                                                            {loeData.cost_rationale.calculation_method && (
                                                                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                                                        Calculation Method
                                                                    </Typography>
                                                                    {loeData.cost_rationale.calculation_method.steps?.length > 0 && (
                                                                        <Box component="ol" sx={{ m: 0, pl: 3, mb: 1 }}>
                                                                            {loeData.cost_rationale.calculation_method.steps.map((step: string, idx: number) => (
                                                                                <Typography component="li" variant="caption" color="text.secondary" key={idx} sx={{ mb: 0.5 }}>
                                                                                    {step.replace(/^\d+\.\s*/, '')}
                                                                                </Typography>
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                    {loeData.cost_rationale.calculation_method.data_sources?.length > 0 && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            <strong>Data Sources:</strong> {loeData.cost_rationale.calculation_method.data_sources.join(' • ')}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })()}
                                    
                                    {/* Consultant Days Table */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" fontWeight={600}>Consultant Days</Typography>
                                        <Button size="small" startIcon={<AddIcon />} onClick={handleAddConsultant}>
                                            Add Consultant
                                        </Button>
                                    </Box>
                                    <TableContainer sx={{ mb: 4 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: 200 }}><strong>Role</strong></TableCell>
                                                    <TableCell align="center" sx={{ width: 160 }}><strong>Day Rate</strong></TableCell>
                                                    <TableCell align="center" sx={{ width: 150 }}><strong>Days</strong></TableCell>
                                                    <TableCell align="right"><strong>Cost</strong></TableCell>
                                                    <TableCell align="center" sx={{ width: 50 }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {consultants.map(consultant => (
                                                    <TableRow key={consultant.id}>
                                                        <TableCell>
                                                            <FormControl size="small" fullWidth>
                                                                <Select
                                                                    value={consultant.role}
                                                                    onChange={(e) => handleUpdateConsultant(consultant.id, 'role', e.target.value)}
                                                                    sx={{ minWidth: 180 }}
                                                                >
                                                                    {ROLE_OPTIONS.map(opt => (
                                                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <TextField
                                                                type="number"
                                                                size="small"
                                                                value={consultant.rate}
                                                                onChange={(e) => handleUpdateConsultant(consultant.id, 'rate', e.target.value)}
                                                                sx={{ width: 130 }}
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment>
                                                                }}
                                                                inputProps={{ min: 0, style: { textAlign: 'right' } }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <IconButton size="small" onClick={() => handleUpdateConsultant(consultant.id, 'days', consultant.days - 1)}>
                                                                    <RemoveIcon fontSize="small" />
                                                                </IconButton>
                                                                <TextField
                                                                    type="number"
                                                                    size="small"
                                                                    value={consultant.days}
                                                                    onChange={(e) => handleUpdateConsultant(consultant.id, 'days', e.target.value)}
                                                                    sx={{ width: 90, mx: 1 }}
                                                                    inputProps={{ min: 0, style: { textAlign: 'center' } }}
                                                                />
                                                                <IconButton size="small" onClick={() => handleUpdateConsultant(consultant.id, 'days', consultant.days + 1)}>
                                                                    <AddIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {getCurrencySymbol()}{(consultant.days * consultant.rate).toLocaleString()}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <IconButton 
                                                                size="small" 
                                                                color="error" 
                                                                onClick={() => handleRemoveConsultant(consultant.id)}
                                                                disabled={consultants.length <= 1}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                    <TableCell colSpan={2}><strong>Subtotal</strong></TableCell>
                                                    <TableCell align="center"><strong>{calculateTotalDays()} days</strong></TableCell>
                                                    <TableCell align="right"><strong>{getCurrencySymbol()}{calculateConsultantCost().toLocaleString()}</strong></TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    {/* Additional Costs Table */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" fontWeight={600}>Additional Costs</Typography>
                                        <Button size="small" startIcon={<AddIcon />} onClick={handleAddLineItem}>
                                            Add Item
                                        </Button>
                                    </Box>
                                    <TableContainer sx={{ mb: 4 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Item</strong></TableCell>
                                                    <TableCell align="right" sx={{ width: 200 }}><strong>Amount ({getCurrencySymbol()})</strong></TableCell>
                                                    <TableCell align="center" sx={{ width: 60 }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {costLineItems.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={item.name}
                                                                onChange={(e) => handleUpdateLineItem(index, 'name', e.target.value)}
                                                                fullWidth
                                                                variant="standard"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <TextField
                                                                type="number"
                                                                size="small"
                                                                value={item.amount}
                                                                onChange={(e) => handleUpdateLineItem(index, 'amount', e.target.value)}
                                                                sx={{ width: 150 }}
                                                                inputProps={{ min: 0, style: { textAlign: 'right' } }}
                                                                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>{getCurrencySymbol()}</Typography> }}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <IconButton size="small" color="error" onClick={() => handleRemoveLineItem(index)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                    <TableCell><strong>Subtotal</strong></TableCell>
                                                    <TableCell align="right"><strong>{getCurrencySymbol()}{calculateLineItemsTotal().toLocaleString()}</strong></TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    
                                    {/* Grand Total */}
                                    <Divider sx={{ mb: 3 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'primary.dark', borderRadius: 2 }}>
                                        <Typography variant="h6" color="white">Total Estimate</Typography>
                                        <Typography variant="h4" fontWeight={700} color="white">
                                            {getCurrencySymbol()}{calculateGrandTotal().toLocaleString()}
                                        </Typography>
                                    </Box>
                                    
                                    {/* Disclaimer */}
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
                                        This is an indicative estimate for planning purposes only. Actual costs may vary based on project requirements, 
                                        travel needs, and consultant availability. Use the copy button to export this estimate.
                                    </Typography>
                                </Paper>
                            )}
                            
                            {/* Sources & Questions Tab */}
                            {activeTab === 'sources' && (
                                <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                                    <Typography variant="h5" fontWeight={600} gutterBottom>
                                        <SourceIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'secondary.main' }} />
                                        Sources & Evaluation Questions
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Reference proposals and ToR evaluation questions used to generate the methodology
                                    </Typography>
                                    
                                    {/* Source Proposals Section */}
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                            <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            Similar Proposals Referenced ({result.methodology?.sourceProposals?.length || 0})
                                        </Typography>
                                        {result.methodology?.sourceProposals && result.methodology.sourceProposals.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {result.methodology.sourceProposals.map((proposal, index) => {
                                                    // Clean up proposal text - remove brackets, quotes, and trim
                                                    let cleanProposal = typeof proposal === 'string' ? proposal : String(proposal);
                                                    cleanProposal = cleanProposal.replace(/^\[|\]$/g, '').replace(/^["']|["']$/g, '').trim();
                                                    if (!cleanProposal) return null;
                                                    
                                                    return (
                                                        <Card key={index} variant="outlined" sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Chip 
                                                                    label={index + 1} 
                                                                    size="small" 
                                                                    color="primary" 
                                                                    sx={{ mr: 2, minWidth: 32 }} 
                                                                />
                                                                <Typography variant="body1">{cleanProposal}</Typography>
                                                            </Box>
                                                        </Card>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Alert severity="info">No source proposals available.</Alert>
                                        )}
                                    </Box>
                                    
                                    <Divider sx={{ my: 3 }} />
                                    
                                    {/* Evaluation Questions Section */}
                                    <Box>
                                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                            <QuestionAnswerIcon sx={{ mr: 1, color: 'info.main' }} />
                                            ToR Evaluation Questions ({result.methodology?.evaluationQuestions?.length || 0})
                                        </Typography>
                                        {result.methodology?.evaluationQuestions && result.methodology.evaluationQuestions.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {result.methodology.evaluationQuestions.map((question: any, index: number) => {
                                                    // Handle both string and object formats
                                                    const questionText = typeof question === 'string' 
                                                        ? question 
                                                        : question?.question || question?.text || question?.dac_criterion || JSON.stringify(question);
                                                    const dacCriterion = typeof question === 'object' ? question?.dac_criterion : null;
                                                    const isMandatory = typeof question === 'object' ? question?.is_mandatory : null;
                                                    const subQuestions = typeof question === 'object' && Array.isArray(question?.sub_questions) ? question.sub_questions : [];
                                                    
                                                    return (
                                                        <Card key={index} variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                <Chip 
                                                                    label={`Q${index + 1}`} 
                                                                    size="small" 
                                                                    color={isMandatory ? 'error' : 'info'} 
                                                                    sx={{ mr: 2, mt: 0.5 }} 
                                                                />
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="body1" fontWeight={500}>{questionText}</Typography>
                                                                    {dacCriterion && (
                                                                        <Chip label={dacCriterion} size="small" variant="outlined" sx={{ mt: 1, mr: 1 }} />
                                                                    )}
                                                                    {isMandatory && (
                                                                        <Chip label="Mandatory" size="small" color="error" variant="outlined" sx={{ mt: 1 }} />
                                                                    )}
                                                                    {subQuestions.length > 0 && (
                                                                        <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                                                                            {subQuestions.map((sq: string, sqIdx: number) => (
                                                                                <Typography key={sqIdx} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                                                    • {sq}
                                                                                </Typography>
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Card>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Alert severity="info">No evaluation questions extracted from the ToR.</Alert>
                                        )}
                                    </Box>
                                </Paper>
                            )}
                            
                        </Box>
                    </Fade>
                )}
            </>
        </Box>
    );
};

export default ProposalAssistantPage;
